/**
 * Utility functions for TableView dynamic height calculations
 * Helps prevent unnecessary white space when there are no records
 * and provides optimal height scaling for better UX
 * 
 * Added overflow control functionality:
 * - showOverflow: boolean (default: true) - Controls whether to show scrollbar and limit height
 * - When false: Shows all rows without scrollbar, ignores maxVisibleRows and maxContainerHeight
 * - When true: Shows scrollbar after maxVisibleRows, respects height limits
 */

export interface TableHeightConfig {
  headerHeight?: number;
  rowHeight?: number;
  minPadding?: number;
  maxVisibleRows?: number;
  containerExtraSpace?: number;
  maxContainerHeight?: number;
  showOverflow?: boolean;
}

const DEFAULT_CONFIG: Required<TableHeightConfig> = {
  headerHeight: 40,
  rowHeight: 40,
  minPadding: 0,
  maxVisibleRows: 5,
  containerExtraSpace: 100,
  maxContainerHeight: 400,
  showOverflow: true,
};

/**
 * Calculate dynamic TableView height based on number of records
 * @param recordCount - Number of records in the table
 * @param config - Optional configuration for height calculations
 * @returns Height string in pixels (e.g., "112px")
 */
export const calculateTableViewHeight = (
  recordCount: number,
  config: TableHeightConfig = {}
): string => {
  const { headerHeight, rowHeight, minPadding, maxVisibleRows, showOverflow } = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  const minHeight = headerHeight + minPadding;

  if (recordCount === 0) {
    return `${minHeight}px`;
  }

  // If showOverflow is false, show all rows without height restriction
  if (!showOverflow) {
    const calculatedHeight = headerHeight + (recordCount * rowHeight) + minPadding;
    return `${calculatedHeight}px`;
  }

  // Show up to maxVisibleRows records, then use scrollbar
  const visibleRows = Math.min(recordCount, maxVisibleRows);
  const calculatedHeight = headerHeight + (visibleRows * rowHeight) + minPadding;

  return `${calculatedHeight}px`;
};

/**
 * Calculate dynamic container height to prevent excessive white space
 * @param recordCount - Number of records in the table
 * @param config - Optional configuration for height calculations
 * @returns Height string in pixels (e.g., "220px")
 */
export const calculateContainerHeight = (
  recordCount: number,
  config: TableHeightConfig = {}
): string => {
  const { containerExtraSpace, maxContainerHeight, showOverflow } = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  if (recordCount === 0) {
    return '120px'; // Minimal height when no records
  }

  const tableHeight = parseInt(calculateTableViewHeight(recordCount, config));
  const dynamicHeight = tableHeight + containerExtraSpace;

  // If showOverflow is false, don't restrict container height with maxContainerHeight
  if (!showOverflow) {
    return `${dynamicHeight}px`;
  }

  return `${Math.min(dynamicHeight, maxContainerHeight)}px`;
};

/**
 * Determine if scrollbar should be shown based on record count and showOverflow setting
 * @param recordCount - Number of records in the table
 * @param maxVisibleRows - Maximum visible rows before scrollbar (default: 5)
 * @param showOverflow - Whether to show overflow/scrollbar (default: true)
 * @returns 'auto' if scrollbar needed and allowed, 'hidden' otherwise
 */
export const calculateTableOverflow = (
  recordCount: number,
  maxVisibleRows: number = DEFAULT_CONFIG.maxVisibleRows,
  showOverflow: boolean = DEFAULT_CONFIG.showOverflow
): 'auto' | 'hidden' => {
  if (!showOverflow) {
    return 'hidden'; // Never show scrollbar if showOverflow is false
  }
  return recordCount > maxVisibleRows ? 'auto' : 'hidden';
};

/**
 * Get complete TableView styling configuration
 * @param recordCount - Number of records in the table
 * @param config - Optional configuration for height calculations
 * @returns Object with height, overflow, and other styling properties
 */
export const getTableViewStyles = (
  recordCount: number,
  config: TableHeightConfig = {}
) => {
  const { maxVisibleRows, showOverflow } = { ...DEFAULT_CONFIG, ...config };
  const height = calculateTableViewHeight(recordCount, config);

  return {
    height,
    maxWidth: '100%',
    overflow: calculateTableOverflow(recordCount, maxVisibleRows, showOverflow),
  };
};

/**
 * Hook-like function for complete table height management
 * Returns all necessary values for implementing dynamic height
 * @param recordCount - Number of records in the table
 * @param config - Optional configuration for height calculations
 */
export const useTableViewHeight = (
  recordCount: number,
  config: TableHeightConfig = {}
) => {
  const { maxVisibleRows, showOverflow } = { ...DEFAULT_CONFIG, ...config };
  const tableHeight = calculateTableViewHeight(recordCount, config);
  const containerHeight = calculateContainerHeight(recordCount, config);
  const tableStyles = getTableViewStyles(recordCount, config);
  const shouldShowScrollbar = showOverflow && recordCount > maxVisibleRows;

  return {
    tableHeight,
    containerHeight,
    tableStyles,
    shouldShowScrollbar,
    recordCount,
    showOverflow,
  };
};